import { Box, Center, Loader, Tabs } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { tKeys, useTranslate } from 'locales/index';
import { type ReactNode, useState } from 'react';
import { Document, Page } from 'react-pdf';
import HTML from 'shared/components/HTML/HTML';

type ReminderWizardPreviewProps = {
  email?: string;
  pdf?: string;
  isEmailLoading?: boolean;
  isPdfLoading?: boolean;
  activeTab: string;
  onTabChange: (tab: string | null) => void;
};

export const ReminderWizardPreview = ({
  email,
  pdf,
  isEmailLoading,
  isPdfLoading,
  activeTab,
  onTabChange,
}: ReminderWizardPreviewProps) => {
  const t = useTranslate();
  const { ref: pdfFrameRef, width: measuredPdfWidth } = useElementSize();
  // hidden panels measure 0; keep the last real width so the page keeps its size
  const [pdfWidth, setPdfWidth] = useState(0);
  if (measuredPdfWidth > 0 && measuredPdfWidth !== pdfWidth) setPdfWidth(measuredPdfWidth);

  return (
    // activity mode (the default) tears down hidden-panel effects, destroying react-pdf's worker
    <Tabs value={activeTab} onChange={onTabChange} keepMountedMode="display-none">
      <Tabs.List>
        <Tabs.Tab value="email">{t(tKeys.EMAIL)}</Tabs.Tab>
        <Tabs.Tab value="pdf">PDF</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="email" pt="sm">
        <A4Frame isLoading={isEmailLoading} loadingLabel={t(tKeys.LOADING)}>
          {email ? <HTML html={email} /> : null}
        </A4Frame>
      </Tabs.Panel>

      <Tabs.Panel value="pdf" pt="sm">
        <A4Frame isLoading={isPdfLoading} loadingLabel={t(tKeys.LOADING)}>
          <Box ref={pdfFrameRef} w="100%">
            {pdf ? (
              <Document file={`data:application/pdf;base64,${pdf}`}>
                <Page
                  pageNumber={1}
                  width={pdfWidth > 0 ? pdfWidth : undefined}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </Document>
            ) : null}
          </Box>
        </A4Frame>
      </Tabs.Panel>
    </Tabs>
  );
};

const A4_ASPECT_RATIO = '210 / 297';

type A4FrameProps = {
  isLoading?: boolean;
  loadingLabel: string;
  children: ReactNode;
};

const A4Frame = ({ isLoading, loadingLabel, children }: A4FrameProps) => (
  <Box pos="relative" style={{ aspectRatio: A4_ASPECT_RATIO, width: '100%', maxHeight: '60vh' }}>
    <Box bg="var(--mantine-color-white)" h="100%" style={{ overflow: 'auto' }}>
      {children}
    </Box>
    {isLoading && (
      <Center pos="absolute" inset={0} bg="rgba(255, 255, 255, 0.7)">
        <Loader role="progressbar" aria-label={loadingLabel} />
      </Center>
    )}
  </Box>
);
